<script setup>
import { ref, inject, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import TextNodes from "@Components/TextNodes/TextNodes.vue";
import { treeToRows } from "@Components/TextNodes/utils";
import Step from "@Components/Stepper/Step.vue";
import { TrashIcon, ArrowPathIcon, PlusIcon } from "@heroicons/vue/20/solid";
import { useForm } from "@/form";
import pLimit from "p-limit";
import _chunk from "lodash/chunk";
import { getTitleStyle } from "@Utils/project";
import { countRequests } from "@Utils/requests";

const props = defineProps(["modelValue", "step"]);
defineEmits(["update:modelValue", "next", "back", "skip"]);

const { t: __ } = useI18n();
const requestsSettings = inject("requestsSettings");

const nodes = ref();
const processing = ref(false);
const limit = pLimit(10);

const getTitleStyleValue = () => {
    let style = undefined;
    if (requestsSettings.value.custom_style !== undefined && requestsSettings.value.custom_style.length > 0) {
        style = requestsSettings.value.custom_style;
    } else {
        style = getTitleStyle(requestsSettings.value.style)?.name;
    }

    return style;
};

const setProcessing = (value) => {
    if (value) {
        processing.value = true;
        nodes.value.setLoading(true);
    } else {
        processing.value = false;
        nodes.value.setLoading(false);
    }
};

const generate = async (query, length) => {
    if (length === undefined) {
        length = 1;
    }

    let chunks = _chunk(query.split("\n"), 50);
    const promises = [];

    chunks.forEach((chunk) => {
        promises.push(
            limit(async () => {
                const data = await useForm({
                    engine: "bulk",
                    style: getTitleStyleValue(),
                    query: chunk.join("\n"),
                    length: length,
                    language: props.modelValue.language,
                }).post("requests_generate");

                return data;
            })
        );
    });

    const response = { requests: [] };
    (await Promise.all(promises)).forEach((result) => {
        response.requests = response.requests.concat(result.requests ?? []);
    });

    return response;
};

const refresh = async () => {
    if (processing.value) return;

    if (requestsSettings.value.keyword === undefined || requestsSettings.value.keyword.length === 0) return;

    setProcessing(true);

    try {
        const response = await generate(requestsSettings.value.keyword);
        nodes.value.replaceRows(response.requests);
    } catch (e) {}

    setProcessing(false);
};

const rewrite = async (title) => {
    return await useForm({
        title: title,
        engine: "bulk",
        language: props.modelValue.language,
    }).post("requests_rewrite");
};

const actions = {
    header: [],
    footer: [],
    row: [
        {
            icon: ArrowPathIcon,
            tooltip: __("Réécrire"),
            label: null,
            canRun: (action, { getRow }) => {
                // verify that the action is not already processing
                if (action.getProcessing()) return false;

                // verify that the row is not empty
                const row = getRow();
                if (row.title.length === 0) return false;

                return true;
            },
            onBegin: (action) => {
                action.setProcessing(true);
            },
            onHandle: async (action, { getRow, updateRow }) => {
                // call the rewrite
                try {
                    let row = getRow();
                    const response = await rewrite(row.title);
                    row.title = response.title;
                    updateRow(row);
                } catch (e) {}
            },
            onComplete: (action) => {
                action.setProcessing(false);
            },
        },
        {
            icon: PlusIcon,
            tooltip: __("Générez plus de titres en relation"),
            label: null,
            canRun: (action, { getRow }) => {
                // verify that the action is not already processing
                if (action.getProcessing()) return false;

                // verify that the row is not empty
                const row = getRow();
                if (row.title.length === 0) return false;

                return true;
            },
            onBegin: (action) => {
                action.setProcessing(true);
            },
            onHandle: async (action, { getRow, getIndex }) => {
                // fetch requests
                try {
                    const row = getRow();
                    const index = getIndex();
                    const response = await generate(row.title, 5);

                    if (response.requests.length > 0) {
                        let depth = row.depth;

                        nodes.value.addRowsAt(treeToRows(response.requests, depth), index + 1);
                    }
                } catch (e) {}
            },
            onComplete: (action) => {
                action.setProcessing(false);
            },
        },
        {
            icon: TrashIcon,
            tooltip: __("Supprimer"),
            label: null,
            onHandle: (action, { deleteRow }) => {
                deleteRow();
            },
        },
    ],
};

onMounted(() => {
    if (props.modelValue.requests.length === 0) {
        if (requestsSettings.value.disable_rewrite_title !== undefined && requestsSettings.value.disable_rewrite_title) {
            if (requestsSettings.value.keyword.length > 0) {
                let requests = requestsSettings.value.keyword.split("\n");
                requests = requests.map((r) => r.trim());
                requests = requests.filter((r) => r.length > 0);
                requests = requests.map((r) => {
                    return {
                        title: r,
                        meta: {
                            keyword: r,
                        },
                    };
                });
                nodes.value.replaceRows(requests);
            }
        } else {
            refresh();
        }
    }
});
</script>

<template>
    <Step class="px-4 lg:px-8 sm:px-6" :step="step" @next="$emit('next')" @back="$emit('back')" @skip="$emit('skip')">
        <template #header>
            <div class="mx-auto max-w-3xl">
                <h3 class="text-4xl font-semibold leading-tight text-center text-gray-900 dark:text-white">
                    {{ __("Vos titres") }}
                </h3>
            </div>
        </template>
        <div class="mx-auto flex flex-col">
            <TextNodes ref="nodes" v-model="modelValue.requests" :actions="actions" sortable-enabled :single-mode="countRequests(modelValue.requests) > 1000" />
        </div>
    </Step>
</template>
